import React, { useMemo, useState } from 'react';
import rehypeRaw from 'rehype-raw'; // Enable raw HTML parsing
import 'katex/dist/katex.min.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import './MessageFormatter.css';
import { Highlight, themes } from 'prism-react-renderer';
import Chart from '../Report/Components/Chart/Chart';
import Table from '../Report/Components/Table/Table';
import {ReactComponent as ClockIcon} from '../../assets/icons/clock-icon.svg';
import {ReactComponent as BoltIcon} from '../../assets/icons/bolt-icon.svg';
import './Spinner.css';
import Popup from '../Popup/Popup';

const NOTIFICATION_LABELS = {
  buildForecast: 'Forecast Built',
  rollforward: 'Rollforward Complete',
};

const gfmPlugins = [[remarkGfm, { singleTilde: false }]];
const markdownPlugins = [
  [remarkGfm, { singleTilde: false }],
  [remarkMath, { singleDollarTextMath: false }],
];

function parseSystemNotification(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const tagMatch = raw.match(/<system_notification\s([^>]*)>([\s\S]*?)<\/system_notification>/);
  if (!tagMatch) return null;
  const attrs = tagMatch[1];
  const body = tagMatch[2];
  const get = (key) => { const m = attrs.match(new RegExp(`${key}="([^"]*)"`)); return m ? m[1] : null; };
  const summaryMatch = body.match(/<summary>([\s\S]*?)<\/summary>/);
  return {
    type: get('type'),
    id: get('id'),
    date: get('date'),
    summary: summaryMatch ? summaryMatch[1].trim() : '',
  };
}

// Removed unused minHeight constant after switching to Chart component

const preprocessMessage = (message) => {
  try {
    // Ensure block equations ($$...$$) have newlines before and after.
    message = message.replace(/\$\$(.*?)\$\$/gs, (_, content) => `\n\n$$\n${content.trim()}\n$$\n\n`);
    // Convert inline equations from \(...\) to $...$ for proper KaTeX rendering.
  message = message.replace(/\\\((.*?)\\\)/g, (_, content) => `$${content.trim()}$`);
    // Convert block equations from \[...\] to $$...$$ format.
    message = message.replace(/\\\[(.*?)\\\]/gs, (_, content) => `\n\n$$\n${content.trim()}\n$$\n\n`);
    // Trim extra newlines to avoid unnecessary spaces.
    return message.replace(/\n{3,}/g, "\n\n").trim();
  } catch (error) {
    return message;
  }
};

const MessageFormatter = React.memo(({ message, setTile, renderChart, renderBullets = false, isStreaming = false, onReportSnippetClick }) => {
  // Detect whether this message (or any of its streamed chunks) contains an explanation trace
  const hasExplanationTrace = useMemo(() => {
    try {
      if (!message) return false;
      // If message is already an object
      if (typeof message === 'object' && message !== null) {
        if (Array.isArray(message)) {
          return message.some((chunk) => !!(chunk && (chunk.explanation_trace || (chunk.toolContent && chunk.toolContent.explanation_trace))));
        }
        return !!message.explanation_trace;
      }
      // If message is a string, see if it parses to JSON
      const parsed = JSON.parse(message);
      if (Array.isArray(parsed)) {
        return parsed.some((chunk) => !!(chunk && (chunk.explanation_trace || (chunk.toolContent && chunk.toolContent.explanation_trace))));
      }
      if (parsed && typeof parsed === 'object') {
        return !!parsed.explanation_trace;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [message]);

  // Extract explanation details for the popup (tables, queries, rationale, sql)
  const explanationDetails = useMemo(() => {
    try {
      if (!message) return null;
      let candidate = null;

      const pullFromChunk = (chunk) => {
        if (!chunk) return null;
        const et = chunk.explanation_trace || (chunk.toolContent && chunk.toolContent.explanation_trace);
        if (et) {
          return {
            tables_used: et.tables_used || [],
            queries_used: et.queries_used || [],
            rationale: et.rationale || '',
            sql: chunk.sql || ''
          };
        }
        return null;
      };

      if (typeof message === 'object' && message !== null) {
        if (Array.isArray(message)) {
          for (const c of message) {
            const found = pullFromChunk(c);
            if (found) { candidate = found; break; }
          }
          return candidate;
        }
        if (message.explanation_trace) {
          return {
            tables_used: message.explanation_trace.tables_used || [],
            queries_used: message.explanation_trace.queries_used || [],
            rationale: message.explanation_trace.rationale || '',
            sql: message.sql || ''
          };
        }
        return null;
      }
      // String: parse json
      const parsed = JSON.parse(message);
      if (Array.isArray(parsed)) {
        for (const c of parsed) {
          const found = pullFromChunk(c);
          if (found) { candidate = found; break; }
        }
        return candidate;
      }
      if (parsed && typeof parsed === 'object' && parsed.explanation_trace) {
        return {
          tables_used: parsed.explanation_trace.tables_used || [],
          queries_used: parsed.explanation_trace.queries_used || [],
          rationale: parsed.explanation_trace.rationale || '',
          sql: parsed.sql || ''
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  }, [message]);

  const [showWorkOpen, setShowWorkOpen] = useState(false);
  const [notificationExpanded, setNotificationExpanded] = useState(false);

  const systemNotification = useMemo(() => {
    if (!message || typeof message !== 'string') return null;
    return parseSystemNotification(message);
  }, [message]);

  // Build ordered segments: interleave chips and text in the order they appear in the chunk array.
  // This ensures "thought → text → tool call → thought → text" renders correctly instead of
  // stacking all chips at the top and all text below.
  const orderedSegments = useMemo(() => {
    try {
      if (!message || typeof message !== 'string') return null;
      const parsed = JSON.parse(message);
      if (!Array.isArray(parsed)) return null;

      // Find last report-snippet index for deduplication (keep only the latest)
      let lastSnippetIdx = -1;
      for (let i = 0; i < parsed.length; i++) {
        const chunk = parsed[i];
        if (!chunk) continue;
        const t = chunk.content || chunk.txt || '';
        if (typeof t === 'string' && t.includes('```report-snippet')) lastSnippetIdx = i;
      }

      // Build a map of completed tool calls (toolCallId → done label from tr chunk)
      const trMap = new Map();
      for (const ch of parsed) {
        if (ch?.type === 'tr' && ch.toolCallId) {
          trMap.set(ch.toolCallId, ch.label || null);
        }
      }

      const segments = [];
      let currentText = '';

      for (let i = 0; i < parsed.length; i++) {
        const chunk = parsed[i];
        if (!chunk) continue;

        if (chunk.type === 'rs' && chunk.subtype === 'reasoning_summary') {
          if (currentText.trim()) {
            segments.push({ type: 'text', content: currentText });
            currentText = '';
          }
          const raw = (chunk.content || chunk.txt || '').replace(/\n/g, '').trim();
          if (raw) {
            const isNewFormat = raw === 'Thinking...' || raw.startsWith('Thought for ');
            const text = isNewFormat ? raw : 'Thought for a moment.';
            segments.push({ type: 'chip', chipType: 'reasoning', text, literal: true });
          }
        } else if (chunk.type === 'tc') {
          if (currentText.trim()) {
            segments.push({ type: 'text', content: currentText });
            currentText = '';
          }
          const toolCallId = chunk.toolCallId;
          const isDone = toolCallId && trMap.has(toolCallId);
          const doneLabel = isDone ? trMap.get(toolCallId) : null;
          const rawLabel = (isDone && doneLabel)
            ? doneLabel
            : (chunk.label || chunk.toolContent?.function?.name || null);
          if (rawLabel) {
            const text = isDone ? rawLabel : rawLabel.replace(/[.…]+$/, '').trim();
            if (text) segments.push({ type: 'chip', chipType: 'tool', text, literal: isDone, done: isDone });
          }
        } else if (chunk.type === 'tc_preview') {
          // Early preview chip — only show if no real tc has arrived for this tool call yet
          const hasRealTc = parsed.some(c => c.type === 'tc' && c.toolCallId === chunk.toolCallId);
          if (!hasRealTc) {
            if (currentText.trim()) {
              segments.push({ type: 'text', content: currentText });
              currentText = '';
            }
            const label = chunk.label || 'Tool call';
            const text = label.replace(/[.…]+$/, '').trim();
            if (text) segments.push({ type: 'chip', chipType: 'tool', text, literal: false, done: false });
          }
        } else if (chunk.type === 'tr') {
          // Consumed via trMap above
        } else {
          const text = chunk.content || chunk.txt || '';
          if (text) {
            // Drop older report-snippets; keep only the last one
            if (typeof text === 'string' && text.includes('```report-snippet') && i !== lastSnippetIdx) continue;
            currentText += text;
          }
        }
      }

      // Flush any remaining text
      if (currentText.trim()) {
        segments.push({ type: 'text', content: currentText });
      }

      return segments.length > 0 ? segments : null;
    } catch (e) {
      return null;
    }
  }, [message]);

  // Parse message content if it's in the new JSON format
  const processedContent = useMemo(() => {
    try {
      // First, ensure message is a string
      if (typeof message !== 'string') {
        // If message is an object with chartCode property (from InsightDetail.jsx)
        if (message && message.chartCode) {
          return message.chartCode;
        }
        // For other object types, try to stringify them
        return JSON.stringify(message);
      }
      
      // Check if message is a string that can be parsed as JSON
      try {
        const parsedMessage = JSON.parse(message);
        
        // If it's an array (our new format), keep all text but only the last report-snippet
        if (Array.isArray(parsedMessage)) {
          const parts = [];
          let lastSnippetIdx = -1;

          // First pass: collect contents and remember last snippet index
          // Skip 'rs' / 'tc' / 'tr' chunks — rendered separately as action chips above
          const contents = parsedMessage.map((chunk) => {
            if (!chunk) return '';
            if (chunk.type === 'rs') return ''; // rendered as reasoning chip
            if (chunk.type === 'tc') return ''; // rendered as tool call chip
            if (chunk.type === 'tr') return ''; // tool result — not displayed
            return (chunk.content || chunk.txt || '');
          });

          for (let i = 0; i < contents.length; i++) {
            const text = contents[i];
            if (typeof text === 'string' && text.includes('```report-snippet')) {
              lastSnippetIdx = i;
            }
          }

          // Second pass: push everything except earlier snippets
          contents.forEach((text, idx) => {
            if (!text) return;
            if (typeof text === 'string' && text.includes('```report-snippet') && idx !== lastSnippetIdx) {
              return; // drop older snippets
            }
            parts.push(text);
          });

          return parts
            .filter((p) => p !== '')
            .join('\n\n')
            .trim();
        }
        
        // If it's not an array but still valid JSON, return the original message
        return message;
      } catch (e) {
        // Not valid JSON, treat as regular markdown
        return message;
      }
      
    } catch (error) {
      console.error("Error processing message:", error);
      return typeof message === 'string' ? message : '';
    }
  }, [message]);
  
  // Preprocess message to handle math formulas
  const processedMessage = useMemo(() => {
    if (!processedContent) return '';
    return preprocessMessage(processedContent);
  }, [processedContent]);
  
  // Helper: extract language id from a class string (supports hyphens)
  const getLanguageFromClass = (className) => {
    const match = /(?:^|\s)language-([^\s]+)/.exec(className || '');
    return match ? match[1] : null;
  };

  // Check if a code block is tagged as JSON
  const isJsonLanguage = (className) => getLanguageFromClass(className) === 'json';
  
  // Check if a code block is tagged as Chart
  const isChartLanguage = (className) => getLanguageFromClass(className) === 'chart';

  // Check if a code block is tagged as Table
  const isTableLanguage = (className) => getLanguageFromClass(className) === 'table';

  // Check if a code block is tagged as Report Snippet
  const isReportSnippetLanguage = (className) => getLanguageFromClass(className) === 'report-snippet';
  // Check if a code block is tagged as Task Snippet
  const isTaskSnippetLanguage = (className) => getLanguageFromClass(className) === 'task-snippet';

  const wrapperClass = useMemo(() => {
    if (!hasExplanationTrace || isStreaming) return 'mf-wrapper';
    return 'mf-wrapper has-explanation';
  }, [hasExplanationTrace, isStreaming]);

  // ReactMarkdown component overrides — defined once and reused for each text segment
  const markdownComponents = {
        // Wrap tables in a scrollable container so only the table scrolls, not the chat
        table: ({ node, children, ...props }) => {
          try {
            // Parse markdown table AST to columns/rows
            const getText = (n) => {
              if (!n) return '';
              if (typeof n === 'string') return n;
              if (Array.isArray(n.children)) return n.children.map(getText).join('');
              if (n.value) return String(n.value);
              return '';
            };
            const findChildrenByTag = (root, tag) => {
              if (!root || !Array.isArray(root.children)) return [];
              return root.children.filter((c) => c && c.tagName === tag);
            };
            const theads = findChildrenByTag(node, 'thead');
            const tbodys = findChildrenByTag(node, 'tbody');
            let headerCells = [];
            if (theads.length > 0) {
              const headTrs = findChildrenByTag(theads[0], 'tr');
              if (headTrs.length > 0) {
                const ths = headTrs[0].children?.filter((c) => c.tagName === 'th') || [];
                headerCells = ths.map((th) => getText(th));
              }
            }
            const bodyTrs = tbodys.length > 0 ? findChildrenByTag(tbodys[0], 'tr') : [];
            let rows = bodyTrs.map((tr) => (tr.children || []).filter((c) => c.tagName === 'td').map((td) => getText(td)));
            // If no thead, treat first row as header
            if (headerCells.length === 0 && rows.length > 0) {
              headerCells = rows[0];
              rows = rows.slice(1);
            }
            // Infer column types
            const inferType = (values) => {
              const cleaned = values.map((v) => String(v || '').trim());
              const allCurrency = cleaned.every((v) => /^\$?\s?-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(v));
              if (allCurrency) return 'currency';
              const allPercent = cleaned.every((v) => /^-?\d+(\.\d+)?%$/.test(v));
              if (allPercent) return 'percent';
              const allNumber = cleaned.every((v) => v === '' || !isNaN(Number(v)));
              if (allNumber) return 'number';
              return 'string';
            };
            const columns = headerCells.map((h, i) => ({ name: String(h || ''), selector: `col_${i}`, type: inferType(rows.map(r => r[i])) }));
            const columnData = rows.map((r) => columns.map((_, i) => r[i] ?? ''));
            const component = { header: '', subheader: '', columns, columnData };
            return (
              <Table component={component} />
            );
          } catch (e) {
            return <table {...props}>{children}</table>;
          }
        },
        // Heading overrides to render proper semantic elements with class hooks
        h1: ({ children }) => <h1>{children}</h1>,
        h2: ({ children }) => <h2>{children}</h2>,
        h3: ({ children }) => <h3>{children}</h3>,
        h4: ({ children }) => <h4>{children}</h4>,
        h5: ({ children }) => <h5>{children}</h5>,
        h6: ({ children }) => <h6>{children}</h6>,
        // Render paragraphs and include an inline show-work button on the last paragraph via CSS
        p: ({ node, children }) => (
          <div className="mf-paragraph" style={{ marginTop: '0px', marginBottom: '10px' }}>
            {children}
            {!isStreaming && hasExplanationTrace && (
              <button className="show-work-chip" onClick={() => setShowWorkOpen(true)}>show work</button>
            )}
          </div>
        ),
        // Enhanced bullet point rendering.
        ul: ({ node, ordered, children, ...props }) => {
          return (
            <ul className={renderBullets ? 'enhanced-bullets' : ''} {...props}>
              {children}
            </ul>
          );
        },
        ol: ({ node, ordered, children, ...props }) => {
          return (
            <ol className={renderBullets ? 'enhanced-bullets' : ''} {...props}>
              {children}
            </ol>
          );
        },
        li: ({ node, children }) => (
          <li className={renderBullets ? 'enhanced-bullet-item' : ''} style={{ marginBottom: '0px', marginTop: '0px' }}>
            {children}
          </li>
        ),
        code({ node, inline, className, children, ...props }) {
          // If inline code or no language is specified, render normally.
          if (inline || !className) {
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
          
          // Handle Chart code blocks.
          if (className && isChartLanguage(className)) {
            let chartData = String(children).replace(/\n$/, '');
            try {
              // If renderChart prop is provided, use it to render the chart.
              if (renderChart) {
                return renderChart(chartData);
              }
              // Otherwise, render using Chart (which embeds Tile).
              try {
                const parsed = JSON.parse(chartData);
                // Normalize to Chart.jsx component props
                let componentConfig = {};
                if (parsed && typeof parsed === 'object') {
                  // New unified chart_json schema: { header, subheader, type, data, options? }
                  if ((parsed.type && parsed.data) || parsed.options || parsed.header || parsed.subheader) {
                    componentConfig = { header: parsed.header, subheader: parsed.subheader, chartJson: parsed };
                  } else if (parsed.chartJson) {
                    componentConfig = parsed; // already in component shape
                  } else if (
                    parsed.type ||
                    (parsed.data && !Array.isArray(parsed.data)) ||
                    parsed.options
                  ) {
                    // Legacy Tile-only config
                    componentConfig = { chartJson: parsed };
                  } else {
                    // Likely a component config (chartType/xAxis/series/data/etc.)
                    componentConfig = parsed;
                  }
                }
                return <Chart component={componentConfig} />;
              } catch (e) {
                // If not valid JSON, pass through as chartJson string
                return <Chart component={{ chartJson: chartData }} />;
              }
            } catch (error) {
              console.error("Error rendering chart:", error);
              // Fallback to syntax highlighting if chart rendering fails.
              return (
                <Highlight
                  theme={themes.oneDark}
                  code={chartData}
                  language="json"
                >
                  {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                      {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              );
            }
          }

          // Handle Table code blocks.
          if (className && isTableLanguage(className)) {
            let tableData = String(children).replace(/\n$/, '');
            try {
              const parsed = JSON.parse(tableData);
              // Convert to Table.jsx shape if needed
              if (parsed && typeof parsed === 'object') {
                // New-only schema handling
                // Shape 1: { header, subheader, data: [ {selector: value, ...} ], columns: [{ name, selector, type }], grouping, total }
                if (Array.isArray(parsed.data) && Array.isArray(parsed.columns)) {
                  const columns = parsed.columns.map((c, i) => ({ name: c.name || c.label || `Col ${i+1}`, selector: c.selector || c.id || c.key || `col_${i}`, type: c.type || 'string' }));
                  const columnData = parsed.data.map((row) => columns.map((col) => row && Object.prototype.hasOwnProperty.call(row, col.selector) ? row[col.selector] : ''));
                  // Normalize groupings: if provided as array of strings, forward as groupings; Table.jsx will adapt
                  const componentCfg = {
                    header: parsed.header || '',
                    subheader: parsed.subheader || '',
                    columns,
                    columnData,
                    data: parsed.data,
                    grouping: parsed.grouping || null,
                    groupings: parsed.groupings || null,
                    footer: parsed.footer || null
                  };
                  return <Table component={componentCfg} />;
                }
                // Shape 2: { columns, rows }
                if (parsed.columns && parsed.rows) {
                  const columns = (parsed.columns || []).map((c, i) => ({ name: c.label || c.name || `Col ${i+1}`, selector: c.id || c.key || `col_${i}`, type: c.type || 'string' }));
                  const columnData = (parsed.rows || []).map(r => r);
                  return <Table component={{ header: parsed.header || '', subheader: parsed.subheader || '', columns, columnData, grouping: parsed.grouping || null, groupings: parsed.groupings || null, footer: parsed.footer || null }} />;
                }
                // Shape 3: Already in definition shape
                if (parsed.columns && parsed.columnData) {
                  return <Table component={{ header: parsed.header || '', subheader: parsed.subheader || '', columns: parsed.columns, columnData: parsed.columnData, data: parsed.data, grouping: parsed.grouping || null, groupings: parsed.groupings || null, footer: parsed.footer || null }} />;
                }
              }
              // Fallback to syntax highlight
              throw new Error('Unsupported table json');
            } catch (error) {
              console.error("Error rendering table block:", error);
              return (
                <Highlight theme={themes.oneDark} code={tableData} language="json">
                  {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                      {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              );
            }
          }

          // Handle Report Snippet code blocks.
          if (className && isReportSnippetLanguage(className)) {
            let snippetData = String(children).replace(/\n$/, '');
            try {
              const snippet = JSON.parse(snippetData) || {};
              const status = snippet.status || 'completed';
              const isLoading = status === 'in-progress';
              const title = isLoading ? (snippet.title || 'Generating Report...') : (snippet.title || 'Report snippet');
              const subtitle = isLoading ? '' : (snippet.subtitle || '');
              return (
                <button type="button" className={`report-snippet ${isLoading ? 'loading' : ''}`} disabled={isLoading} onClick={() => {
                  if (!isLoading && typeof onReportSnippetClick === 'function') {
                    try { onReportSnippetClick(snippet); } catch (e) { /* noop */ }
                  }
                }}>
                  <div className="report-snippet-title">
                    {isLoading && <span className="snippet-spinner" />}
                    {title}
                  </div>
                  {subtitle ? <div className="report-snippet-subtitle">{subtitle}</div> : null}
                </button>
              );
            } catch (error) {
              // Fallback to syntax highlighting if parsing fails.
              return (
                <Highlight
                  theme={themes.oneDark}
                  code={snippetData}
                  language="json"
                >
                  {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                      {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              );
            }
          }

          // Handle Task Snippet code blocks.
          if (className && isTaskSnippetLanguage(className)) {
            let snippetData = String(children).replace(/\n$/, '');
            try {
              const snippet = JSON.parse(snippetData) || {};
              const status = snippet.status || 'completed';
              const isLoading = status === 'in-progress';
              const title = snippet.title || 'Task';
              const subtitle = isLoading ? '' : (snippet.subtitle || '');
              return (
                <button type="button" className={`task-snippet ${isLoading ? 'loading' : ''}`} disabled={isLoading} onClick={() => {
                  if (!isLoading && typeof onReportSnippetClick === 'function') {
                    try { onReportSnippetClick(snippet); } catch (e) { /* noop */ }
                  }
                }}>
                  <div className="task-snippet-row">
                    <ClockIcon className="task-snippet-icon" />
                    <div className="task-snippet-texts">
                      <div className="task-snippet-title">{title}</div>
                      {subtitle ? <div className="task-snippet-subtitle">{subtitle}</div> : null}
                    </div>
                  </div>
                </button>
              );
            } catch (error) {
              return (
                <Highlight
                  theme={themes.oneDark}
                  code={snippetData}
                  language="json"
                >
                  {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                      {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              );
            }
          }
          
          // Handle JSON code blocks.
          if (className && isJsonLanguage(className)) {
            let jsonString = String(children).replace(/\n$/, '');
            return <pre>{jsonString}</pre>;
            try {
              const parsedJson = JSON.parse(jsonString);
              if (setTile) {
                setTile(jsonString);
              }
              // Decide whether this JSON is a full chartJson or a component config
              let componentConfig = {};
              if (parsedJson && typeof parsedJson === 'object') {
                if (parsedJson.chartJson) {
                  componentConfig = parsedJson;
                } else if (
                  parsedJson.type ||
                  (parsedJson.data && !Array.isArray(parsedJson.data)) ||
                  parsedJson.options
                ) {
                  componentConfig = { chartJson: parsedJson };
                } else {
                  componentConfig = parsedJson;
                }
              }
              return <Chart component={componentConfig} />;
            } catch (error) {
              // Attempt to "correct" the JSON if parsing fails.
              const correctedJsonString = jsonString;
              try {
                JSON.parse(correctedJsonString);
                if (setTile) {
                  setTile(correctedJsonString);
                }
                return <Chart component={{ chartJson: correctedJsonString }} />;
              } catch (correctError) {
                // If still invalid, fallback to a syntax highlighted block.
                return (
                  <Highlight
                    theme={themes.oneDark}
                    code={jsonString}
                    language="json"
                  >
                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                      <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                        {tokens.map((line, i) => (
                          <div key={i} {...getLineProps({ line })}>
                            {line.map((token, key) => (
                              <span key={key} {...getTokenProps({ token })} />
                            ))}
                          </div>
                        ))}
                      </pre>
                    )}
                  </Highlight>
                );
              }
            }
          }
          
          // Render other code blocks using Prism
          const match = /(?:^|\s)language-([^\s]+)/.exec(className || '');
          return (
            <Highlight
              theme={themes.oneDark}
              code={String(children)}
              language={match ? match[1] : 'text'}
            >
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <pre className={className} style={{ ...style, padding: '1em', borderRadius: '4px', overflow: 'auto' }}>
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          );
        },
  };

  let lastTextSegmentIdx = -1;
  let hasAnyContent = false;
  if (orderedSegments) {
    for (let i = orderedSegments.length - 1; i >= 0; i--) {
      if (orderedSegments[i].type === 'text' && orderedSegments[i].content.trim()) {
        if (lastTextSegmentIdx === -1) lastTextSegmentIdx = i;
        hasAnyContent = true;
      }
      if (orderedSegments[i].type === 'chip') hasAnyContent = true;
    }
  } else {
    hasAnyContent = !!processedMessage?.trim();
  }

  if (systemNotification) {
    if (systemNotification.type === 'buildForecast') {
      return (
        <div className="mf-wrapper">
          <ReactMarkdown
            remarkPlugins={gfmPlugins}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {systemNotification.summary || ''}
          </ReactMarkdown>
        </div>
      );
    }

    const label = NOTIFICATION_LABELS[systemNotification.type] || systemNotification.type;
    const dateStr = systemNotification.date
      ? new Date(systemNotification.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    return (
      <div className="system-notification-tile">
        <button
          type="button"
          className={`sn-header${notificationExpanded ? ' expanded' : ''}`}
          onClick={() => setNotificationExpanded((v) => !v)}
        >
          <BoltIcon className="sn-icon" />
          <span className="sn-label">{label}</span>
          {dateStr && <span className="sn-date">{dateStr}</span>}
          <span className={`sn-chevron${notificationExpanded ? ' open' : ''}`} />
        </button>
        {notificationExpanded && systemNotification.summary && (
          <div className="sn-body">
            <ReactMarkdown
              remarkPlugins={gfmPlugins}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {systemNotification.summary}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${wrapperClass}${isStreaming ? ' is-streaming' : ''}`}>
      {orderedSegments ? (
        orderedSegments.map((segment, idx) => {
          if (segment.type === 'chip') {
            const isChipDone = segment.done ||
              (segment.chipType === 'reasoning' && segment.text !== 'Thinking...');
            const isActive = isStreaming && !isChipDone && (
              segment.chipType === 'tool' || idx === orderedSegments.length - 1
            );
            const baseText = segment.literal
              ? segment.text.replace(/\.{2,}$/, '')
              : segment.text;
            return (
              <div key={idx} className="reasoning-chips">
                <div className={`reasoning-chip ${isActive ? 'active' : ''}`}>
                  {isActive ? baseText : (segment.literal ? segment.text : `${segment.text}...`)}
                  {isActive && (
                    <span className="animated-dots">
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                      <span className="dot">.</span>
                    </span>
                  )}
                </div>
              </div>
            );
          }
          if (segment.type === 'text') {
            let content = preprocessMessage(segment.content);
            const hasChipAfter = orderedSegments.slice(idx + 1).some(s => s.type === 'chip');
            if (isStreaming && idx === lastTextSegmentIdx && !hasChipAfter) {
              content += '<span class="streaming-cursor"></span>';
            }
            return (
              <ReactMarkdown
                key={idx}
                remarkPlugins={markdownPlugins}
                rehypePlugins={[rehypeKatex, rehypeRaw]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            );
          }
          return null;
        })
      ) : (
        processedMessage?.trim() ? (
          <ReactMarkdown
            remarkPlugins={markdownPlugins}
            rehypePlugins={[rehypeKatex, rehypeRaw]}
            components={markdownComponents}
          >
            {isStreaming ? processedMessage + '<span class="streaming-cursor"></span>' : processedMessage}
          </ReactMarkdown>
        ) : null
      )}

      {isStreaming && !hasAnyContent && (
        <div className="typing-indicator">
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
        </div>
      )}

    {showWorkOpen && explanationDetails && (
      <Popup
        onClose={() => setShowWorkOpen(false)}
        content={(
          <div className="show-work-modal" onClick={(e) => e.stopPropagation()}>
              <button className="sw-close" onClick={() => setShowWorkOpen(false)}>Close</button>
            <div className="sw-header">AGENT REASONING</div>
            <div className="sw-rationale">
              {explanationDetails.rationale || 'This is the agent’s reasoning for how it built the query and what key assumptions were used.'}
            </div>
            <div className="sw-detail">
              <div className="sw-left">
                <div className="sw-title">TABLES</div>
                <div className="sw-chips">
                  {(explanationDetails.tables_used || []).map((t, i) => (
                    <span key={`table-${i}`} className="sw-chip sw-chip-blue">{t}</span>
                  ))}
                </div>
                <div className="sw-title" style={{ marginTop: '12px' }}>VERIFIED QUERIES</div>
                <div className="sw-chips">
                  {(explanationDetails.queries_used || []).map((q, i) => (
                    <span key={`vq-${i}`} className="sw-chip sw-chip-purple">{q}</span>
                  ))}
                </div>
              </div>
              <div className="sw-right">
                <div className="sw-title">SQL USED</div>
                <Highlight
                  theme={themes.oneLight}
                  code={explanationDetails.sql || ''}
                  language="sql"
                >
                  {({ className, style, tokens, getLineProps, getTokenProps }) => (
                    <pre className={`sw-sql ${className}`} style={{ ...style }}>
                      {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              </div>
            </div>
          </div>
        )}
      />
    )}
    </div>
  );
});

export default MessageFormatter;
